import { useCallback, useEffect, useState } from 'react';

export const NAV_EXPANDED_KEY = 'heyroot.admin.nav.expanded';

/**
 * Reads persisted expanded-group keys. Every failure mode — absent, corrupt,
 * wrong shape, storage access denied (private windows, blocked site data) —
 * degrades to "nothing expanded" rather than throwing during render.
 */
export function readExpandedGroups(): string[] {
  try {
    const raw = localStorage.getItem(NAV_EXPANDED_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is string => typeof v === 'string');
  } catch {
    return [];
  }
}

export function writeExpandedGroups(keys: string[]): void {
  try {
    localStorage.setItem(NAV_EXPANDED_KEY, JSON.stringify(keys));
  } catch {
    // Persistence is a convenience; a blocked store must not break the nav.
  }
}

/**
 * Expanded-group state. The group owning the current route is always treated
 * as expanded, so navigating into a collapsed group never hides the active
 * item; the user's explicit toggles persist across sessions.
 */
export function useNavGroupState(activeKey: string | null): {
  isExpanded: (key: string) => boolean;
  toggle: (key: string) => void;
} {
  // activeKey seeds initial state (so navigating straight to a route reveals
  // its group) but is not a permanent override — once seeded, the group is
  // just another entry in `expanded` and can be collapsed like any other.
  const [expanded, setExpanded] = useState<string[]>(() => {
    const persisted = readExpandedGroups();
    if (activeKey && !persisted.includes(activeKey)) return [...persisted, activeKey];
    return persisted;
  });

  useEffect(() => {
    writeExpandedGroups(expanded);
  }, [expanded]);

  // Navigating to a NEW activeKey should reveal that group without
  // disturbing the user's other explicit choices. The `prev.includes` guard
  // returns the same array reference when nothing changes, so this never
  // fires the write-effect above in a loop.
  useEffect(() => {
    if (!activeKey) return;
    setExpanded((prev) => (prev.includes(activeKey) ? prev : [...prev, activeKey]));
  }, [activeKey]);

  const toggle = useCallback((key: string) => {
    setExpanded((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  }, []);

  const isExpanded = useCallback((key: string) => expanded.includes(key), [expanded]);

  return { isExpanded, toggle };
}
